import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextareaAutosize,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import {
  DatePicker,
  DesktopTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Close, Delete, Edit } from "@mui/icons-material";
import SelectManufacturingOrder from "./SelectManufacturingOrder";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import SelectMultipleShopEmployees from "./SelectMultipleShopEmployees";
import SelectEventOrder from "./SelectEventOrder";
import SelectEventPhases from "./SelectEventPhases";
import SelectWorkStation from "./SelectWorkStation";
import { AuthContext } from "../../../../context/AuthContext";
import useShopEmployees from "../../../../hooks/useShopEmployees";
import Tiptap from "../../../../components/Tip/Tiptap";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const EditJobDialog = ({
  dialogData,
  handleClose,
  handleSubmit,
  errorMessage,
}) => {
  const theme = useTheme();
  const { authToken, ezorder, decoded } = useContext(AuthContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const validationSchema = Yup.object({
    eventEntity: Yup.mixed()
      .oneOf(["IND", "RFL"], "Entity required")
      .required("Entity required"),
    date: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid date",
      (value) => dayjs.isDayjs(value) && value.isValid(),
    ),
    startDate: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid start time",
      (value) => dayjs.isDayjs(value) && value.isValid(),
    ),
    endDate: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid stop time",
      (value) => dayjs.isDayjs(value) && value.isValid(),
    ),
    order: Yup.object()
      .nullable()
      .when("eventEntity", {
        is: "IND", // alternatively: (val) => val == true
        then: Yup.object()
          .nullable()
          .shape({
            id: Yup.number().required("Select an Industry Job"),
          })
          .required("Select an Industry Job"),
        otherwise: Yup.object().nullable(),
      }),
    manufacturingOrderId: Yup.number("Select a mfg")
      .nullable()
      .when("eventEntity", {
        is: "RFL", // alternatively: (val) => val == true
        then: Yup.number().nullable().required("Select an RFL Job"),
        otherwise: Yup.number().nullable(),
      }),
    workStation: Yup.object().nullable().shape({
      id: Yup.number().nullable(),
    }),
    assignedTo: Yup.array().of(
      Yup.object().shape({
        id: Yup.number(),
      }),
    ),
    // .min(1, `Assign at least one person`),
    subPhases: Yup.array().of(
      Yup.object().shape({
        id: Yup.number(),
      }),
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      rflWorkOrder:
        dialogData.calendarEvent && dialogData.calendarEvent.id
          ? dialogData.calendarEvent.rflWorkOrder
          : null,
      // rflTasks:
      //   dialogData.calendarEvent && dialogData.calendarEvent.id
      //     ? dialogData.calendarEvent.rflTasks
      //     : null,
      id:
        dialogData.calendarEvent && dialogData.calendarEvent.id
          ? dialogData.calendarEvent.id
          : null,
      eventEntity: dialogData.calendarEvent
        ? dialogData.calendarEvent.eventEntity
        : "",
      eventNotes: dialogData.calendarEvent
        ? dialogData.calendarEvent.eventNotes
        : "",
      date: dialogData.calendarEvent ? dialogData.calendarEvent.date : null,
      startDate: dialogData.calendarEvent
        ? dialogData.calendarEvent.startDate
        : null,
      endDate: dialogData.calendarEvent
        ? dialogData.calendarEvent.endDate
        : null,
      order: dialogData.calendarEvent ? dialogData.calendarEvent.order : null,
      manufacturingOrderId: dialogData.calendarEvent
        ? dialogData.calendarEvent.manufacturingOrderId
        : null,
      workStation: dialogData.calendarEvent
        ? dialogData.calendarEvent.workStation
        : null,
      assignedTo: dialogData.calendarEvent
        ? dialogData.calendarEvent.assignedTo
        : [],
      subPhases: dialogData.calendarEvent
        ? dialogData.calendarEvent.subPhases
        : [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Submit
      console.log("SUBMIT", values);
      handleSubmit(values);
    },
  });

  if (formik && formik.values)
    return (
      <Dialog
        open={dialogData.open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth={true}
      >
        <>
          <DialogTitle sx={{ display: "flex", flexDirection: "row" }}>
            {formik.values.eventEntity == "IND" ? (
              <Box sx={{ flex: 1 }}>
                {formik.values.order &&
                  formik.values.order.company &&
                  formik.values.order.project &&
                  formik.values.order.orderStatus && (
                    <>
                      IND Job #{formik.values.order.id} -{" "}
                      {formik.values.order.company.name}
                      <DialogContentText sx={{ display: "block" }}>
                        {formik.values.order.company.isVendorOrSupplier
                          ? "Vendor/Supplier Customer"
                          : "Project"}
                        : {formik.values.order.project.name}
                      </DialogContentText>
                      {formik.values.order.vendorProjectName && (
                        <DialogContentText sx={{ display: "block" }}>
                          Project: {formik.values.order.vendorProjectName}
                        </DialogContentText>
                      )}
                      {formik.values.order.subProject && (
                        <DialogContentText sx={{ display: "block" }}>
                          Sub-Project: {formik.values.order.subProject}
                        </DialogContentText>
                      )}
                      <DialogContentText sx={{ display: "block" }}>
                        {formik.values.order.poNumber
                          ? `PO#: ${formik.values.order.poNumber}`
                          : ""}
                      </DialogContentText>
                    </>
                  )}
              </Box>
            ) : (
              <Box sx={{ flex: 1 }}>
                {formik.values.rflWorkOrder
                  ? `RFL Work Order #${formik.values.rflWorkOrder.id}`
                  : `RFL Mfg. Order #${formik.values.manufacturingOrderId}`}
              </Box>
            )}
            <Box>
              <IconButton onClick={handleClose} sx={{}}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box>
              <Box sx={{ width: "100%" }}>
                {/* Start Date */}
                <Box sx={{ marginTop: "12px" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      sx={{ marginRight: "10px", marginBottom: "15px" }}
                      format="MM/DD/YYYY"
                      label="Date"
                      value={formik.values.date}
                      onChange={(newValue) => {
                        let date = dayjs(newValue)
                          .tz(DEFAULT_TIMEZONE)
                          .startOf("day");
                        let startDate = date.startOf("day").add(7, "hours");
                        let endDate = date.startOf("day").add(8, "hours");

                        formik.setValues((prev) => ({
                          ...prev,
                          date,
                          startDate,
                          endDate,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          fullWidth
                          sx={{}}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.date && Boolean(formik.errors.date)
                          }
                          helperText={
                            formik.errors.date && `${formik.errors.date}`
                          }
                          onFocus={(e) => {
                            e.target.select();
                          }}
                          {...params}
                        />
                      )}
                    />
                    {formik.values.date && formik.values.date.isValid() && (
                      <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <DesktopTimePicker
                          label="From"
                          value={formik.values.startDate}
                          onChange={(newValue) => {
                            formik.setValues((prev) => ({
                              ...prev,
                              startDate: dayjs(newValue).tz(DEFAULT_TIMEZONE),
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              fullWidth
                              sx={{ marginTop: "8px", marginRight: "8px" }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.startDate &&
                                Boolean(formik.errors.startDate)
                              }
                              helperText={
                                formik.errors.startDate &&
                                `${formik.errors.startDate}`
                              }
                              onFocus={(e) => {
                                e.target.select();
                              }}
                              {...params}
                            />
                          )}
                          // maxTime={formik.values.endDate}
                        />
                        <DesktopTimePicker
                          label="To"
                          value={formik.values.endDate}
                          onChange={(newValue) => {
                            formik.setValues((prev) => ({
                              ...prev,
                              endDate: dayjs(newValue).tz(DEFAULT_TIMEZONE),
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              fullWidth
                              sx={{ marginTop: "8px", marginLeft: "8px" }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.endDate &&
                                Boolean(formik.errors.endDate)
                              }
                              helperText={
                                formik.errors.endDate &&
                                `${formik.errors.endDate}`
                              }
                              onFocus={(e) => {
                                e.target.select();
                              }}
                              {...params}
                            />
                          )}
                          // minTime={formik.values.endDate}
                        />
                      </Box>
                    )}
                  </LocalizationProvider>
                </Box>
              </Box>
              {/* Employee Select */}
              <Box sx={{ marginTop: "12px" }}>
                <SelectMultipleShopEmployees
                  size="small"
                  fullWidth
                  label="Assign To"
                  value={formik.values.assignedTo}
                  onUpdate={(users) => {
                    formik.setFieldValue("assignedTo", users);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.assignedTo &&
                    Boolean(formik.errors.assignedTo)
                  }
                  helperText={
                    formik.errors.assignedTo && `${formik.errors.assignedTo}`
                  }
                  onFocus={(e) => {
                    e.target.select();
                  }}
                />
              </Box>
              {/* Work Station Select */}
              {formik.values.eventEntity == "RFL" && (
                <Box sx={{ marginTop: "12px" }}>
                  <SelectWorkStation
                    size="small"
                    fullWidth
                    label="Work Station"
                    value={formik.values.workStation}
                    onUpdate={(workStation) => {
                      formik.setFieldValue("workStation", workStation);
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.assignedTo &&
                      Boolean(formik.errors.assignedTo)
                    }
                    helperText={
                      formik.errors.assignedTo && `${formik.errors.assignedTo}`
                    }
                    onFocus={(e) => {
                      e.target.select();
                    }}
                  />
                </Box>
              )}

              <Divider sx={{ marginY: "10px" }} />

              <Typography variant="h6">Tasks</Typography>
              {/* Jobs - select */}
              {/* {formik.values.eventEntity == "IND" && (
                <Box sx={{ marginTop: "12px" }}>
                  {formik.values.order && (
                    <SelectEventPhases
                      jobId={formik.values.order.id}
                      scheduledSubPhases={formik.values.subPhases}
                      onUpdate={(subPhases) => {
                        formik.setFieldValue("subPhases", subPhases);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.subPhases &&
                        Boolean(formik.errors.subPhases)
                      }
                      helperText={
                        formik.errors.subPhases && `${formik.errors.subPhases}`
                      }
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  )}
                </Box>
              )} */}
              {/* Event Description*/}
              {formik.values.eventEntity !== "RFL" && (
                <Box
                  style={{
                    flex: 1,
                    // paddingLeft: 16, paddingRight: 16
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TextareaAutosize
                      // placeholder="Add instructions or end of day progress notes here"
                      style={{
                        marginTop: "8px",
                        // color: "#fff",
                        flex: 1,
                      }}
                      fullWidth
                      minRows={3}
                      value={formik.values.eventNotes}
                      onChange={(e) => {
                        // setFabricatorNote(e.target.value);
                        formik.setFieldValue("eventNotes", e.target.value);
                      }}
                    />
                  </Box>
                </Box>
              )}

              {formik.values.eventEntity !== "IND" &&
                formik.values.rflWorkOrder && (
                  <Box>
                    <Typography sx={{ marginTop: "16px" }}>
                      Subject: {formik.values.rflWorkOrder.subject}
                    </Typography>
                    <Tiptap
                      editable={false}
                      editorContent={
                        formik.values.rflWorkOrder &&
                        formik.values.rflWorkOrder.detailsJson
                          ? JSON.parse(formik.values.rflWorkOrder.detailsJson)
                          : ""
                      }
                    />
                  </Box>
                )}

              {errorMessage ? (
                <Typography sx={{ color: "#d32f2f", marginTop: "5px" }}>
                  {errorMessage}
                </Typography>
              ) : (
                <></>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                formik.handleSubmit();
              }}
            >
              Update
            </Button>
          </DialogActions>
        </>
      </Dialog>
    );
};

export default EditJobDialog;
